import { Loading } from '@getslot/ui-web/notice'
import type { QueryClient } from '@tanstack/react-query'
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
} from '@tanstack/react-router'
import { Suspense } from 'react'
import { type LaneKey, parseLane } from './features/applications/lanes'
import { AuthGate } from './shared/auth/auth-gate'
import { RouteError } from './shared/ui/route-error'
import { Shell } from './shared/ui/shell'

interface RouterContext {
  queryClient: QueryClient
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <AuthGate>
      <Suspense fallback={<Loading />}>
        <Shell>
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
        </Shell>
      </Suspense>
    </AuthGate>
  ),
  errorComponent: RouteError,
})

const text = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() !== '' ? value.trim().slice(0, 100) : undefined

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./features/dashboard/dashboard-page'), 'DashboardPage'),
  errorComponent: RouteError,
})

const applicationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/applications',
  validateSearch: (search: Record<string, unknown>): { lane?: LaneKey; view?: 'client'; q?: string } => ({
    lane: parseLane(search.lane),
    view: search.view === 'client' ? 'client' : undefined,
    q: text(search.q),
  }),
  component: lazyRouteComponent(() => import('./features/applications/applications-page'), 'ApplicationsPage'),
  errorComponent: RouteError,
})

const cardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/applications/$id',
  component: lazyRouteComponent(() => import('./features/applications/card-page'), 'CardPage'),
  errorComponent: RouteError,
})

const clientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clients',
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({ q: text(search.q) }),
  component: lazyRouteComponent(() => import('./features/clients/clients-page'), 'ClientsPage'),
  errorComponent: RouteError,
})

const clientRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clients/$id',
  component: lazyRouteComponent(() => import('./features/clients/client-page'), 'ClientPage'),
  errorComponent: RouteError,
})

const workersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/workers',
  component: lazyRouteComponent(() => import('./features/workers/workers-page'), 'WorkersPage'),
  errorComponent: RouteError,
})

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: lazyRouteComponent(() => import('./features/settings/settings-page'), 'SettingsPage'),
  errorComponent: RouteError,
})

const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users',
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({ q: text(search.q) }),
  component: lazyRouteComponent(() => import('./features/users/users-page'), 'UsersPage'),
  errorComponent: RouteError,
})

const routeTree = rootRoute.addChildren([
  dashboardRoute,
  applicationsRoute,
  cardRoute,
  clientsRoute,
  clientRoute,
  workersRoute,
  settingsRoute,
  usersRoute,
])

export function buildRouter(queryClient: QueryClient) {
  return createRouter({ routeTree, context: { queryClient }, defaultPreload: 'intent' })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof buildRouter>
  }
}
